import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { AnnotationCanvas, type IHoveredMark, type IPaintedItem } from './AnnotationCanvas/AnnotationCanvas';
import { AnnotationDraft } from './AnnotationDraft/AnnotationDraft';
import { ControlsRow } from './ControlsRow/ControlsRow';
import type { AnnotationDraft as Draft } from '../hooks/useAnnotationDraft';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './AnnotationsBody.module.css';

interface IAnnotationsBodyProps {
  /** The zoom hook's measuring box — the page owns zoom for both modes. */
  ref?: Ref<HTMLDivElement>;
  content: ReactNode;
  contentKey: string | number; // Re-measures the overlay when this changes: a page flip resizes the canvas
  contentReady: boolean;
  failed: boolean; // The bytes or the page failed to render — nothing can be drawn or read

  /** The marks on this page, from the annotations currently checked. */
  painted: readonly IPaintedItem[];
  onHover: (hovered: IHoveredMark | null) => void;

  /** Present while drawing; the tools are mounted only then. */
  draft: Draft | null;

  zoom: number | null;
  onZoomChange: (pct: number) => void;
  onZoomDelta: (direction: 1 | -1) => void;

  /** Page navigation, for a multi-page PDF. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;

  shown: number;
  total: number;
}

/**
 * The reading surface: the document, the marks over it, and the strip beneath
 * that says where you are in it. Drawing swaps the read-only canvas for the
 * editor — everything else stays exactly where it was, which is why the two
 * modes feel like one page rather than two screens.
 */
export function AnnotationsBody({
  ref: viewerRef,
  content, contentKey, contentReady, failed,
  painted, onHover, draft,
  zoom, onZoomChange, onZoomDelta,
  page, pageCount, onPageChange, shown, total,
}: IAnnotationsBodyProps) {
  const { t } = useTranslation();
  const backdropItems = painted.map((p) => p.item);

  return (
    <main className={styles.stage}>
      <ScrollArea orientation="both" className={styles.viewer} viewportClassName={styles.viewerBody} viewportRef={viewerRef}>
        {failed ? (
          <p className={styles.loadError} role="alert">{t('documents.markup.loadError')}</p>
        ) : draft ? (
          <AnnotationDraft
            draft={draft}
            contentKey={contentKey}
            backdropItems={backdropItems}
            contentReady={contentReady}
            onZoomDelta={onZoomDelta}
          >
            {content}
          </AnnotationDraft>
        ) : (
          // Nothing is painted before the document is on screen: marks over an
          // empty viewer read as a glitch, and the skeleton does not cover a
          // document larger than the pane.
          <AnnotationCanvas contentKey={contentKey} painted={contentReady ? painted : []} onHover={onHover}>
            {content}
          </AnnotationCanvas>
        )}
      </ScrollArea>
      {!failed && !contentReady && (
        <div className={styles.skeleton} role="status" aria-label={t('documents.previewLoading')} />
      )}

      {/* Nothing to navigate or zoom until the document is on screen — the
          strip appears with it, not before it, but its space is held. */}
      {!contentReady || failed ? (
        <div className={styles.stripPlaceholder} aria-hidden="true" />
      ) : (
        <ControlsRow
          page={page}
          pageCount={pageCount}
          onPageChange={onPageChange}
          shown={shown}
          total={total}
          zoom={zoom}
          onZoomChange={onZoomChange}
          onZoomDelta={onZoomDelta}
        />
      )}
    </main>
  );
}
